import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { EngagementGrid } from "@/components/calv/EngagementGrid";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/strategic-coordination")({
  head: () => {
    const h = pageHead("strategicCoordination", "en");
    return { meta: h.meta, links: h.links };
  },
  component: StrategyPage,
});

const topics = [
  {
    label: "01 · Marketing strategy & channel mix",
    title: "Which channels carry your business model economically?",
    body: "Methodical budget allocation across SEA, SEO, social, affiliate, email. In-house build vs. agency substance. Channel mix for international expansion from DACH into EU or US markets.",
  },
  {
    label: "02 · Tech stack & data architecture",
    title: "Shopify, WooCommerce, or Shopware — and which CRM behind it?",
    body: "Marketing automation, CDP, server-side tracking setups that survive platform updates. Custom vs. standard. Where AI integrates meaningfully, where it's buzzword theater.",
  },
  {
    label: "03 · Agency & vendor management",
    title: "RFP methodology, performance reviews, agency switches.",
    body: "Substantive RFPs, evaluation of running partnerships, methodical switches without losing campaign history. Ownership of accounts and data.",
  },
  {
    label: "04 · Growth & scaling",
    title: "Where does the actual scaling bottleneck sit?",
    body: "Traffic, conversion rate, margins, operations. Tension between growth and efficiency. Plateau or local maximum. Investments across 12 vs. 36 months.",
  },
  {
    label: "05 · Crisis situations & platform shifts",
    title: "Tracking losses, core updates, penalty situations.",
    body: "Data-protection audits, platform bans. Fast, well-founded framing instead of panic. Experience from comparable situations.",
  },
  {
    label: "06 · Organization & KPI architecture",
    title: "Marketing team structure that holds up over five years.",
    body: "KPIs for managing-director reporting vs. operational view. Cadence between optimization and escalation. Responsibilities, overlaps, interfaces.",
  },
];

const engagements = [
  {
    label: "Strategy Review · one-off",
    title: "3 to 7 days over 2 to 4 weeks.",
    body: "Substantive assessment of one concrete question — marketing setup, tech stack, agency evaluation. Outcome: written assessment with clear recommendations, optionally workshop presentation.",
  },
  {
    label: "Quarterly Sparring · ongoing",
    title: "1 to 2 days per month over 12 months.",
    body: "Jour-fixe rhythm, accompaniment of running decisions, presence at strategic reviews, sparring on acute questions. For an independent voice in the marketing steering circle.",
  },
  {
    label: "RFP & selection support · project-based",
    title: "5 to 15 days over 2 to 4 months.",
    body: "Formulate RFP methodologically, evaluate vendor responses, accompany presentation rounds. For agency selection, shop re-platformings, CRM rollouts.",
  },
  {
    label: "Tech stack audit · one-off",
    title: "5 to 10 days over 3 to 6 weeks.",
    body: "Methodical assessment of existing marketing and data infrastructure — tracking, tools, integrations, configurations. Outcome: findings report with prioritized action plan.",
  },
  {
    label: "Crisis sparring · acute",
    title: "Days to weeks, high-frequency availability.",
    body: "For platform updates, tracking losses, performance drops, data-protection conflicts. Fast framing, methodological diagnosis, aligned immediate measures.",
  },
];

const steps = [
  {
    num: "01",
    title: "Situational framing",
    body: "We understand where you stand economically, technically, and organizationally. Which goals hold up, which assumptions survive scrutiny, which target conflicts are real. Preliminary work, not formalism — without this framing, every recommendation is speculation.",
  },
  {
    num: "02",
    title: "Prioritization of levers",
    body: "In complex setups, the problem is not lack of activity but lack of prioritization. We identify the two to four levers with the highest economic effect — and we name explicitly what should not be pursued.",
  },
  {
    num: "03",
    title: "Coordination of actors",
    body: "We translate between business and technology, between internal teams and external vendors, between short-term and long-term requirements. We make dependencies visible, close responsibility gaps, align actions toward a common goal.",
  },
  {
    num: "04",
    title: "Continuous framing",
    body: "Strategic questions don't arise just once. We accompany decisions over weeks and months, in jour-fixe sessions, ad-hoc requests, or specific escalations. Our standard is clarity, not complexity.",
  },
];

const triggers = [
  {
    label: "Trigger 01",
    title: "Multiple vendors, no overarching coordination.",
    body: "Performance agency, SEO vendor, web agency, shop provider, and internal teams all working in parallel — without anyone methodologically watching the interfaces. Duplication, gaps, mutually obstructing measures.",
  },
  {
    label: "Trigger 02",
    title: "Substantial investment decision ahead.",
    body: "Re-platforming, CRM rollout, agency switch, internationalization, marketing-team build-out. Without external framing, you're dependent on the input of those who have a stake in the outcome.",
  },
  {
    label: "Trigger 03",
    title: "Growth plateau or unclear performance trajectory.",
    body: "ROAS declining, cost-per-lead rising, conversion rate stagnating — without the causes emerging clearly from the available data. A methodical outside view helps that's not captured by operational routines.",
  },
  {
    label: "Trigger 04",
    title: "Crisis situation with need for response.",
    body: "Platform updates, compliance conflicts, data-migration problems, sudden performance drops. Methodological framing is more valuable than activism — and faster available than an RFP process.",
  },
];

const exclusions = [
  {
    label: "Boundary 01",
    title: "No commissions, no reseller models.",
    body: "We don't sell tools, platforms, or licenses. Our recommendation must prove itself solely against your economic outcome — not against a margin arrangement with a vendor.",
  },
  {
    label: "Boundary 02",
    title: "No permanent consulting as a business model.",
    body: "We don't aim to stay around indefinitely. When an engagement has served its sparring purpose, we wind it down methodologically — even when that reduces the basis of business.",
  },
  {
    label: "Boundary 03",
    title: "No advisory without operational substance.",
    body: "We don't give recommendations on topics where we have no own mandate experience. Where our substance ends, we say so openly — and point to substantive externals, without taking commissions for the referral.",
  },
];

function StrategyPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Strategy & Coordination · since 2013"
          headline={<>Strategic sparring, <em>operationally grounded.</em></>}
          lead="We support managing directors and marketing leadership in complex digital setups — on channel-mix decisions, tech-stack selection, agency coordination, and in crisis situations. Independent of commissions, reseller contracts, or sales incentives. Operational depth from our own client mandate work."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "Strategy & Coordination" }]}
        />

        <Section number="02" kicker="Who we are" headline={<>Consulting from <em>operational substance</em>, not from slide decks.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Classical strategy consulting separates thinkers from doers — slides get presented, execution sits elsewhere. Operational agencies, in turn, have their own interests at stake: every recommendation risks expanding their own scope of work.</p>
              <p className="mb-5">We operate differently. Calvarius has been running the operational substance itself since 2013 — performance marketing, SEO, tracking, web development. That experience is what we bring to the sparring engagement. We know how a server-side tracking setup actually gets built, what a Performance Max campaign truly costs to run, when a shop migration takes six months longer than planned.</p>
              <p>That makes us something other than classical consulting. We don't deliver strategy decks — we deliver assessments that hold up against your reality. And we take no commissions, no reseller margins, no sales bonuses — our recommendation must prove itself solely against your economic outcome.</p>
            </div>
          </RevealOnScroll>
          <Callout>Strategy without operational depth is wishful thinking. Operational work without strategic framing is busywork. Both together are substance.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Topics we typically spar on" headline={<>Concrete questions, <em>not generic advisory.</em></>} caption="Six clusters in which Calvarius is regularly called in as sparring partner. The list is not exhaustive, but it shows where our operational experience contributes most directly.">
          <EngagementGrid items={topics} />
        </Section>

        <Section number="04" kicker="Engagement models" headline={<>Concrete <em>formats</em> for concrete situations.</>} caption="Strategic sparring is not a fixed product — engagement depth and duration follow your situation. Which format fits we clarify in the initial conversation.">
          <EngagementGrid items={engagements} />
        </Section>

        <Section variant="tinted" number="05" kicker="Approach" headline={<>Four phases, <em>robust in execution.</em></>}>
          <ProcessFlow steps={steps} />
          <Callout>Good decisions don't come from more opinions — they come from better framing.</Callout>
        </Section>

        <Section number="06" kicker="When this role really pays off" headline={<>Four trigger situations <em>where sparring genuinely creates leverage.</em></>}>
          <EngagementGrid items={triggers} />
        </Section>

        <Section variant="tinted" number="07" kicker="What we deliberately don't do" headline={<>Methodological <em>honesty</em> over generalist promises.</>}>
          <EngagementGrid items={exclusions} />
        </Section>

        <LogoReferences locale="en" number="08" />

        <RelatedServices
          locale="en"
          fromId="strategie-koordination"
          number="09"
          items={relatedBySource["strategie-koordination"]}
        />

        <Section number="10" kicker="Free · approx. 30 minutes">
          <CTABlock
            headline={<>Initial conversation about your <em>concrete situation.</em></>}
            body="In the initial conversation we clarify methodologically whether a sparring engagement fits your situation — and in what format. You outline your situation, we give an initial framing. If mutually sensible: proposal of a fitting engagement model. If we're not the right fit, we'll say so openly."
            primary={{ label: "Request a strategy conversation", to: "/en/contact" }}
            secondary={{ label: "Test the tools", to: "/en/tools" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
